import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { OrbitMotif } from "@/components/OrbitMotif";
import { DownloadAppSection } from "@/components/DownloadAppSection";
import { LegalProSpotlight } from "@/components/LegalProSpotlight";
import { Seo, organizationJsonLd, softwareApplicationJsonLd } from "@/components/Seo";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import {
  MessageSquare,
  Image as ImageIcon,
  Code2,
  Gauge,
  ShieldCheck,
  Globe,
  ArrowRight,
  Sparkles,
  Mic,
  Zap,
  Languages,
  Scale,
} from "lucide-react";

const FEATURE_BLOCKS = [
  { key: "chat", icon: MessageSquare, span: "md:col-span-2", accent: "lime" },
  { key: "image", icon: ImageIcon, span: "md:col-span-1", accent: "navy" },
  { key: "code", icon: Code2, span: "md:col-span-1", accent: "navy" },
  { key: "quotas", icon: Gauge, span: "md:col-span-2", accent: "lime" },
  { key: "secure", icon: ShieldCheck, span: "md:col-span-1", accent: "navy" },
  { key: "multilang", icon: Globe, span: "md:col-span-2", accent: "navy" },
];

export default function Landing() {
  const { t } = useI18n();
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    api.get("/payments/plans").then((r) => {
      if (r.data?.active_promo) setPromo(r.data.active_promo);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Seo
        title={t("seo.landing_title")}
        description={t("seo.landing_description")}
        canonicalPath="/"
        ogImage="/brand/og-default.png"
        jsonLdList={[organizationJsonLd, softwareApplicationJsonLd]}
      />
      <Header />

      {promo && (
        <Link
          to={`/pricing?promo=${promo.code}`}
          className="block bg-[#05073B] text-white transition-colors hover:bg-[#1A1D7A]"
          data-testid="landing-promo-banner"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 px-6 py-2.5 text-sm">
            <Sparkles className="h-3.5 w-3.5 text-[#9ACD32]" />
            <span className="font-medium">
              <span className="font-bold text-[#9ACD32]">{t("pricing.promo_banner_title")}</span>
              {" · "}
              {t("pricing.promo_banner_subtitle", { code: promo.code, remaining: promo.remaining ?? "?" })}
            </span>
            <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </Link>
      )}
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FAFAF8]">
        <OrbitMotif
          className="absolute -right-40 -top-40 h-[900px] w-[900px] orbit-anim"
          color="#9ACD32"
          opacity={0.12}
        />
        <OrbitMotif
          className="absolute -bottom-60 -left-40 h-[700px] w-[700px]"
          color="#0B0E5C"
          opacity={0.08}
        />

        <div className="relative mx-auto max-w-7xl px-6 pb-24 pt-20 lg:px-10 lg:pt-32">
          <div className="max-w-4xl">
            <div
              className="fade-up inline-flex items-center gap-2 rounded-full border border-[#0B0E5C]/15 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#0B0E5C]"
              style={{ animationDelay: "0ms" }}
              data-testid="hero-pretitle"
            >
              <Sparkles className="h-3 w-3 text-[#9ACD32]" />
              {t("landing.hero_pretitle")}
            </div>

            <h1
              className="fade-up mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter text-[#05073B] sm:text-6xl lg:text-7xl"
              style={{ animationDelay: "80ms" }}
              data-testid="hero-title"
            >
              {t("landing.hero_title")}
            </h1>

            <p
              className="fade-up mt-6 max-w-2xl text-lg leading-relaxed text-slate-600"
              style={{ animationDelay: "160ms" }}
              data-testid="hero-subtitle"
            >
              {t("landing.hero_subtitle")}
            </p>

            <div
              className="fade-up mt-10 flex flex-wrap items-center gap-4"
              style={{ animationDelay: "240ms" }}
            >
              <Button
                asChild
                size="lg"
                className="lime-glow rounded-full bg-[#9ACD32] px-8 py-6 text-base font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="hero-cta-primary"
              >
                <Link to="/auth?mode=signup">
                  {t("landing.hero_cta_primary")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-[#0B0E5C] px-8 py-6 text-base font-semibold text-[#0B0E5C] hover:bg-[#0B0E5C] hover:text-white"
                data-testid="hero-cta-secondary"
              >
                <Link to="/pricing">{t("landing.hero_cta_secondary")}</Link>
              </Button>
              <Link
                to="#legal-pro"
                className="group inline-flex items-center gap-2 rounded-full border border-[#D4A937]/40 bg-[#D4A937]/5 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#A67C0F] transition-colors hover:bg-[#D4A937]/15"
                data-testid="hero-cta-legal"
              >
                <Scale className="h-3.5 w-3.5" />
                {t("landing.hero_legal_chip")}
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>

            <div
              className="fade-up mt-16 grid max-w-2xl grid-cols-3 gap-8"
              style={{ animationDelay: "320ms" }}
            >
              <Stat value="11+" label={t("landing.stat_models")} />
              <Stat value="103" label={t("landing.stat_rulings")} />
              <Stat value="GDPR" label={t("landing.stat_gdpr_compact")} />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES (bento) */}
      <section id="features" className="relative bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-16 max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#9ACD32]">
              {t("common.features")}
            </span>
            <h2
              className="mt-3 font-display text-4xl font-extrabold tracking-tight text-[#05073B] sm:text-5xl"
              data-testid="features-title"
            >
              {t("landing.features_title")}
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-slate-600">
              {t("landing.features_subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {FEATURE_BLOCKS.map((blk) => {
              const Icon = blk.icon;
              const isLime = blk.accent === "lime";
              return (
                <Card
                  key={blk.key}
                  className={`group relative overflow-hidden ${blk.span} border-slate-200 p-8 transition-all hover:-translate-y-1 hover:shadow-lg`}
                  data-testid={`feature-${blk.key}`}
                >
                  <div
                    className={`mb-6 flex h-12 w-12 items-center justify-center rounded-xl ${
                      isLime
                        ? "bg-[#9ACD32]/15 text-[#7AAA20]"
                        : "bg-[#0B0E5C]/8 text-[#0B0E5C]"
                    }`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-[#05073B]">
                    {t(`landing.feat_${blk.key}_title`)}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                    {t(`landing.feat_${blk.key}_desc`)}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Voice-powered section — NEW */}
      <LegalProSpotlight />

      <section
        id="voice"
        className="relative overflow-hidden bg-gradient-to-br from-[#0B0E5C] via-[#06082E] to-[#05073B] py-24 lg:py-32"
        data-testid="landing-voice-section"
      >
        <OrbitMotif
          className="absolute inset-0 -m-40 h-[140%] w-[140%]"
          color="#9ACD32"
          opacity={0.08}
        />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
          {/* Left — copy */}
          <div>
            <span
              className="inline-flex items-center gap-2 rounded-full border border-[#9ACD32]/40 bg-[#9ACD32]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#9ACD32]"
              data-testid="voice-pretitle"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {t("landing.voice_pretitle")}
            </span>
            <h2
              className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
              data-testid="voice-title"
            >
              {t("landing.voice_title")}
            </h2>
            <p
              className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300"
              data-testid="voice-subtitle"
            >
              {t("landing.voice_subtitle")}
            </p>

            <ul className="mt-8 space-y-4">
              {[
                { icon: Languages, key: "voice_bullet_languages" },
                { icon: Zap, key: "voice_bullet_fast" },
                { icon: ShieldCheck, key: "voice_bullet_private" },
              ].map(({ icon: Icon, key }) => (
                <li
                  key={key}
                  className="flex items-start gap-3 text-slate-200"
                  data-testid={`voice-bullet-${key}`}
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#9ACD32]/15 text-[#9ACD32]">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[15px] leading-snug">{t(`landing.${key}`)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-[#9ACD32] px-7 font-bold text-[#05073B] shadow-[0_8px_32px_rgba(154,205,50,0.35)] hover:bg-[#A8DF38]"
                data-testid="voice-cta-primary"
              >
                <Link to="/auth?mode=register">
                  {t("landing.voice_cta_primary")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <span className="text-xs uppercase tracking-wider text-slate-500" data-testid="voice-cta-note">
                {t("landing.voice_cta_note")}
              </span>
            </div>
          </div>

          {/* Right — visual mic mock */}
          <div className="relative" data-testid="voice-mock">
            <div className="relative mx-auto aspect-square w-full max-w-md">
              {/* Pulsing rings */}
              <div className="absolute inset-0 animate-ping rounded-full bg-[#9ACD32]/10 [animation-duration:3s]" />
              <div className="absolute inset-6 animate-ping rounded-full bg-[#9ACD32]/15 [animation-duration:2.4s]" />
              <div className="absolute inset-12 rounded-full border border-[#9ACD32]/20" />
              <div className="absolute inset-20 rounded-full border border-[#9ACD32]/30" />
              <div className="absolute inset-28 rounded-full border border-[#9ACD32]/40" />
              {/* Central mic disc */}
              <div className="absolute left-1/2 top-1/2 flex h-32 w-32 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gradient-to-br from-[#9ACD32] to-[#7AAA20] shadow-[0_20px_60px_rgba(154,205,50,0.45)]">
                <Mic className="h-12 w-12 text-[#05073B]" strokeWidth={2.5} />
              </div>
            </div>

            {/* Floating transcript bubble */}
            <Card className="absolute -bottom-2 left-2 max-w-[280px] border-slate-700 bg-[#0F1230] p-4 text-left shadow-2xl sm:left-0">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-[#9ACD32]">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#9ACD32]" />
                {t("landing.voice_transcript_label")}
              </div>
              <p className="mt-2 text-sm leading-snug text-slate-200">
                {t("landing.voice_transcript_example")}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Download / Install as PWA */}
      <DownloadAppSection />

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#05073B] py-24">
        <OrbitMotif
          className="absolute inset-0 -m-40 h-[140%] w-[140%]"
          color="#9ACD32"
          opacity={0.12}
        />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h2
            className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl"
            data-testid="cta-title"
          >
            {t("landing.cta_title")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            {t("landing.cta_subtitle")}
          </p>
          <Button
            asChild
            size="lg"
            className="lime-glow mt-10 rounded-full bg-[#9ACD32] px-10 py-7 text-base font-bold text-[#05073B] hover:bg-[#A8DF38]"
            data-testid="cta-button"
          >
            <Link to="/auth?mode=signup">
              {t("landing.cta_button")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-4xl font-extrabold text-[#0B0E5C]">
        {value}
      </div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
        {label}
      </div>
    </div>
  );
}
